import { useCallback, useMemo, type ReactElement, type ReactNode } from 'react';
import { useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { isUnsafeMethod, readXsrfToken } from '@/lib/csrf';
import { logger } from '@/lib/logger';
import type { UserProfile } from '@/api/mappers';
import { useMe } from '@/hooks/useMe';

import { AuthContext } from '../context';
import type { AuthContextValue, AuthStatus, AuthUser, SignInOptions } from '../types';

/**
 * Real (BFF) auth provider — PTV2-910.
 *
 * Identity is derived from `useMe()` (the `GET /api/v1/me` probe). No tokens
 * exist in the FE: login/logout are full-page navigations to backend
 * endpoints. The 401 → `/login` redirect is owned by `handleUnauthorized()`
 * (invoked from `src/api/http.ts`); this provider therefore has no
 * "session expired" state of its own.
 */
export function RealAuthProvider({ children }: { children: ReactNode }): ReactElement {
  const me = useMe();
  const queryClient = useQueryClient();

  const status = deriveStatus(me);
  const user: AuthUser | null = me.data ? toAuthUser(me.data) : null;

  const signIn = useCallback((opts?: SignInOptions) => {
    const path = opts?.returnTo ?? window.location.pathname + window.location.search;
    window.location.href = `/login?returnTo=${encodeURIComponent(path)}`;
  }, []);

  const signOut = useCallback(async () => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const csrf = readXsrfToken();
    if (csrf !== null && isUnsafeMethod('POST')) {
      headers['X-XSRF-TOKEN'] = csrf;
    }
    let nextUrl = '/';
    try {
      // Raw fetch — http.ts doesn't surface the Location header. The BFF
      // returns 204 + Location pointing at Auth0 /v2/logout to clear the SSO
      // cookie on auth0.com.
      const res = await fetch('/api/v1/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers,
      });
      const loc = res.headers.get('Location');
      if (loc !== null && loc.length > 0) nextUrl = loc;
    } catch (cause) {
      logger.warn('auth.logout.network_error', {
        cause: cause instanceof Error ? cause.name : 'unknown',
      });
    } finally {
      queryClient.clear();
      window.location.href = nextUrl;
    }
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({ mode: 'real', status, user, signIn, signOut }),
    [status, user, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function deriveStatus(me: UseQueryResult<UserProfile>): AuthStatus {
  if (me.isPending) return 'idle';
  if (me.isError) return 'unauthenticated';
  return 'authenticated';
}

function toAuthUser(profile: UserProfile): AuthUser {
  return {
    id: profile.id,
    name: profile.fullName.length > 0 ? profile.fullName : profile.email,
    email: profile.email,
    permissions: Array.from(profile.permissionKeys),
    status: profile.status,
  };
}
